import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiMessageSquare, FiSearch, FiPlus, FiUsers, FiPaperclip, FiSend, FiX, FiInfo, FiArrowLeft, FiFile, FiImage, FiMusic, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { chatAPI } from '../../services/api';
import { connectSocket, getSocket } from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import './Chat.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const FILE_BASE = API_BASE.replace(/\/api\/?$/, '');

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatTime(dateStr, t) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return t ? t('chat.now') : 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatMessageTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateDivider(dateStr, t) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = today - msgDay;
  if (diff === 0) return t ? t('chat.today') : 'Today';
  if (diff === 86400000) return t ? t('chat.yesterday') : 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function getConversationDisplay(conv, currentUserId, t) {
  if (conv.type === 'group') {
    return { name: conv.name || (t ? t('chat.unnamedGroup') : 'Unnamed Group'), initials: conv.name ? conv.name[0].toUpperCase() : 'G' };
  }
  const other = (conv.members || []).find(m => m.user_id !== currentUserId);
  return {
    name: other?.name || (t ? t('chat.unknown') : 'Unknown'),
    initials: getInitials(other?.name),
    userId: other?.user_id
  };
}

function renderMessageText(text, mentions = []) {
  if (!text) return null;
  if (!mentions.length) return text;

  let result = text;
  const parts = [];
  let lastIdx = 0;

  const mentionNames = mentions.map(m => m.name).filter(Boolean);
  mentionNames.sort((a, b) => b.length - a.length);

  for (const name of mentionNames) {
    const pattern = new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
    let match;
    while ((match = pattern.exec(result)) !== null) {
      if (match.index > lastIdx) {
        parts.push({ type: 'text', value: result.slice(lastIdx, match.index) });
      }
      parts.push({ type: 'mention', value: match[0] });
      lastIdx = match.index + match[0].length;
    }
  }

  if (parts.length === 0) return text;
  if (lastIdx < result.length) parts.push({ type: 'text', value: result.slice(lastIdx) });

  return parts.map((p, i) =>
    p.type === 'mention'
      ? <span key={i} className="mention">{p.value}</span>
      : <span key={i}>{p.value}</span>
  );
}

const Chat = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentUserId = user?.user_id;

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarTab, setSidebarTab] = useState('all');
  const [showInfo, setShowInfo] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  const [inputText, setInputText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [pendingMentions, setPendingMentions] = useState([]);

  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatType, setNewChatType] = useState('private');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const chatMessagesRef = useRef(null);

  const activeConv = useMemo(
    () => conversations.find(c => c.id === activeConvId),
    [conversations, activeConvId]
  );

  // ─── Load conversations and users ───
  useEffect(() => {
    loadConversations();
    loadUsers();
  }, []);

  const loadConversations = async () => {
    try {
      const res = await chatAPI.getConversations();
      setConversations(res.data);
    } catch (err) {
      console.error('Load conversations failed:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await chatAPI.getUsers();
      setAllUsers(res.data);
      setOnlineUserIds(res.data.filter(u => u.is_online).map(u => u.user_id));
    } catch (err) {
      console.error('Load users failed:', err);
    }
  };

  // ─── Socket setup ───
  useEffect(() => {
    const socket = connectSocket();

    socket.on('chat:new_message', handleNewMessage);
    socket.on('chat:notification', handleNotification);
    socket.on('chat:mention_notification', handleMentionNotification);
    socket.on('chat:new_conversation', handleNewConversation);
    socket.on('chat:conversation_updated', handleConversationUpdated);
    socket.on('chat:user_online', ({ userId }) => {
      setOnlineUserIds(prev => [...new Set([...prev, userId])]);
    });
    socket.on('chat:user_offline', ({ userId }) => {
      setOnlineUserIds(prev => prev.filter(id => id !== userId));
    });
    socket.on('chat:typing', handleTyping);
    socket.on('chat:stop_typing', handleStopTyping);
    socket.on('chat:added_to_conversation', () => loadConversations());
    socket.on('chat:removed_from_conversation', ({ conversationId }) => {
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConvId === conversationId) setActiveConvId(null);
    });

    return () => {
      socket.off('chat:new_message', handleNewMessage);
      socket.off('chat:notification', handleNotification);
      socket.off('chat:mention_notification', handleMentionNotification);
      socket.off('chat:new_conversation', handleNewConversation);
      socket.off('chat:conversation_updated', handleConversationUpdated);
      socket.off('chat:user_online');
      socket.off('chat:user_offline');
      socket.off('chat:typing', handleTyping);
      socket.off('chat:stop_typing', handleStopTyping);
      socket.off('chat:added_to_conversation');
      socket.off('chat:removed_from_conversation');
    };
  }, [activeConvId]);

  const handleNewMessage = useCallback((message) => {
    setMessages(prev => {
      if (prev.some(m => m.id === message.id)) return prev;
      return [...prev, message];
    });
    setConversations(prev => prev.map(c => {
      if (c.id !== message.conversation_id) return c;
      return {
        ...c,
        last_message_text: message.text,
        last_message_type: message.type,
        last_message_file_name: message.file_name,
        last_message_sender_name: message.sender_name,
        last_message_at: message.created_at,
        unread_count: message.sender_id !== currentUserId
          ? (c.unread_count || 0) + 1
          : c.unread_count
      };
    }));
    scrollToBottom();
  }, [currentUserId]);

  const handleNotification = useCallback(({ conversationId, message }) => {
    if (conversationId === activeConvId) return;
    setConversations(prev => {
      const updated = prev.map(c => {
        if (c.id !== conversationId) return c;
        return {
          ...c,
          last_message_text: message.text,
          last_message_type: message.type,
          last_message_file_name: message.file_name,
          last_message_sender_name: message.sender_name,
          last_message_at: message.created_at,
          unread_count: (c.unread_count || 0) + 1
        };
      });
      return updated.sort((a, b) => {
        const aTime = a.last_message_at || a.created_at;
        const bTime = b.last_message_at || b.created_at;
        return new Date(bTime) - new Date(aTime);
      });
    });
  }, [activeConvId]);

  const handleMentionNotification = useCallback(({ conversationId, mentionedBy }) => {
    if (conversationId === activeConvId) return;
    toast.info(t('chat.mentionNotification', { name: mentionedBy }), { autoClose: 4000 });
  }, [activeConvId]);

  const handleNewConversation = useCallback((conv) => {
    setConversations(prev => {
      if (prev.some(c => c.id === conv.id)) return prev;
      return [conv, ...prev];
    });
  }, []);

  const handleConversationUpdated = useCallback((data) => {
    setConversations(prev => prev.map(c => c.id === data.id ? { ...c, ...data } : c));
  }, []);

  const handleTyping = useCallback(({ conversationId, userId, userName }) => {
    setTypingUsers(prev => ({
      ...prev,
      [conversationId]: { ...(prev[conversationId] || {}), [userId]: userName }
    }));
  }, []);

  const handleStopTyping = useCallback(({ conversationId, userId }) => {
    setTypingUsers(prev => {
      const conv = { ...(prev[conversationId] || {}) };
      delete conv[userId];
      return { ...prev, [conversationId]: conv };
    });
  }, []);

  // ─── Join/leave chat rooms ───
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    conversations.forEach(c => socket.emit('chat:join', c.id));
    return () => {
      conversations.forEach(c => socket.emit('chat:leave', c.id));
    };
  }, [conversations]);

  // ─── Load messages for active conversation ───
  useEffect(() => {
    if (!activeConvId) return;
    loadMessages(activeConvId);
    markAsRead(activeConvId);
  }, [activeConvId]);

  const loadMessages = async (convId, before) => {
    setLoadingMsgs(true);
    try {
      const params = { limit: 50 };
      if (before) params.before = before;
      const res = await chatAPI.getMessages(convId, params);
      if (before) {
        setMessages(prev => [...res.data.messages, ...prev]);
      } else {
        setMessages(res.data.messages);
        setTimeout(scrollToBottom, 50);
      }
      setHasMore(res.data.hasMore);
    } catch (err) {
      console.error('Load messages failed:', err);
    }
    setLoadingMsgs(false);
  };

  const markAsRead = async (convId) => {
    try {
      await chatAPI.markRead(convId);
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
      window.dispatchEvent(new Event('chat-mark-read'));
    } catch (_) {}
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── Send message ───
  const sendMessage = async () => {
    if (!inputText.trim() && !selectedFile) return;
    if (!activeConvId) return;

    const formData = new FormData();
    if (inputText.trim()) formData.append('text', inputText.trim());
    if (selectedFile) formData.append('file', selectedFile);
    if (pendingMentions.length) {
      formData.append('mentions', JSON.stringify(pendingMentions.map(m => m.user_id)));
    }

    setInputText('');
    setSelectedFile(null);
    setPendingMentions([]);

    try {
      await chatAPI.sendMessage(activeConvId, formData);
    } catch (err) {
      toast.error(t('chat.sendFailed'));
      console.error(err);
    }
  };

  // ─── Typing indicator ───
  const emitTyping = () => {
    const socket = getSocket();
    if (!socket || !activeConvId) return;
    socket.emit('chat:typing', { conversationId: activeConvId });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:stop_typing', { conversationId: activeConvId });
    }, 2000);
  };

  // ─── Mention handling ───
  const activeMembers = useMemo(() => {
    if (!activeConv) return [];
    return (activeConv.members || []).filter(m => m.user_id !== currentUserId);
  }, [activeConv, currentUserId]);

  const mentionSuggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return activeMembers.filter(m =>
      m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [mentionQuery, activeMembers]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputText(val);
    emitTyping();

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch && activeConv?.type === 'group') {
      setMentionQuery(atMatch[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (member) => {
    const cursorPos = textareaRef.current?.selectionStart || inputText.length;
    const textBefore = inputText.slice(0, cursorPos);
    const textAfter = inputText.slice(cursorPos);
    const beforeMention = textBefore.replace(/@\w*$/, '');
    const newText = `${beforeMention}@${member.name} ${textAfter}`;
    setInputText(newText);
    setPendingMentions(prev => {
      if (prev.some(m => m.user_id === member.user_id)) return prev;
      return [...prev, member];
    });
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (mentionSuggestions.length > 0 && mentionQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => Math.min(prev + 1, mentionSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionSuggestions[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Create new conversation ───
  const createConversation = async () => {
    if (newChatType === 'private' && selectedUserIds.length !== 1) {
      toast.error(t('chat.selectOneUser'));
      return;
    }
    if (newChatType === 'group' && selectedUserIds.length < 1) {
      toast.error(t('chat.selectAtLeastOneUser'));
      return;
    }
    if (newChatType === 'group' && !newGroupName.trim()) {
      toast.error(t('chat.groupNameRequired'));
      return;
    }

    try {
      const res = await chatAPI.createConversation({
        type: newChatType,
        name: newChatType === 'group' ? newGroupName.trim() : undefined,
        description: newChatType === 'group' ? newGroupDesc.trim() : undefined,
        memberIds: selectedUserIds
      });

      if (!res.data.existing) {
        setConversations(prev => {
          if (prev.some(c => c.id === res.data.id)) return prev;
          return [res.data, ...prev];
        });
      }

      setActiveConvId(res.data.id);
      setMobileShowChat(true);
      closeNewChatModal();
    } catch (err) {
      toast.error(t('chat.createFailed'));
      console.error(err);
    }
  };

  const closeNewChatModal = () => {
    setShowNewChat(false);
    setNewChatType('private');
    setNewGroupName('');
    setNewGroupDesc('');
    setSelectedUserIds([]);
    setUserSearch('');
  };

  const toggleUserSelect = (uid) => {
    if (newChatType === 'private') {
      setSelectedUserIds(prev => prev[0] === uid ? [] : [uid]);
    } else {
      setSelectedUserIds(prev =>
        prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
      );
    }
  };

  // ─── Filtered conversations ───
  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (sidebarTab === 'private') list = list.filter(c => c.type === 'private');
    else if (sidebarTab === 'group') list = list.filter(c => c.type === 'group');

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(c => {
        const display = getConversationDisplay(c, currentUserId, t);
        return display.name.toLowerCase().includes(q);
      });
    }
    return list;
  }, [conversations, sidebarTab, searchTerm, currentUserId]);

  // ─── Typing display ───
  const typingDisplay = useMemo(() => {
    if (!activeConvId) return '';
    const conv = typingUsers[activeConvId] || {};
    const names = Object.values(conv);
    if (!names.length) return '';
    if (names.length === 1) return t('chat.isTyping', { name: names[0] });
    return t('chat.areTyping', { names: names.slice(0, 2).join(', ') });
  }, [typingUsers, activeConvId]);

  // ─── Message groups by date ───
  const messageGroups = useMemo(() => {
    const groups = [];
    let lastDate = null;
    for (const msg of messages) {
      const d = new Date(msg.created_at);
      const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (dateKey !== lastDate) {
        groups.push({ type: 'divider', date: msg.created_at, key: `div-${dateKey}` });
        lastDate = dateKey;
      }
      groups.push({ type: 'message', data: msg, key: `msg-${msg.id}` });
    }
    return groups;
  }, [messages]);

  // ─── Users for new chat modal ───
  const filteredUsers = useMemo(() => {
    return allUsers
      .filter(u => u.user_id !== currentUserId)
      .filter(u => {
        if (!userSearch.trim()) return true;
        const q = userSearch.toLowerCase();
        return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
      });
  }, [allUsers, currentUserId, userSearch]);

  const selectConversation = (convId) => {
    setActiveConvId(convId);
    setMobileShowChat(true);
    setShowInfo(false);
  };

  // ─── Remove member from group ───
  const removeMember = async (userId) => {
    if (!activeConvId) return;
    try {
      await chatAPI.removeMember(activeConvId, userId);
      setConversations(prev => prev.map(c => {
        if (c.id !== activeConvId) return c;
        return { ...c, members: (c.members || []).filter(m => m.user_id !== userId) };
      }));
      toast.success(t('chat.memberRemoved'));
    } catch (err) {
      toast.error(t('chat.removeMemberFailed'));
    }
  };

  const getOtherUserOnline = (conv) => {
    if (conv.type !== 'private') return false;
    const other = (conv.members || []).find(m => m.user_id !== currentUserId);
    return other ? onlineUserIds.includes(other.user_id) : false;
  };

  // ─── Render helpers ───
  const renderFileAttachment = (msg) => {
    const url = msg.file_url
      ? (String(msg.file_url).startsWith('http://') || String(msg.file_url).startsWith('https://')
        ? msg.file_url
        : `${FILE_BASE}${msg.file_url}`)
      : null;
    if (!url) return null;

    if (msg.type === 'image') {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt={msg.file_name} className="msg-image" />
        </a>
      );
    }

    if (msg.type === 'audio') {
      return <audio controls className="msg-audio" src={url} />;
    }

    const icon = msg.type === 'image' ? <FiImage /> : msg.type === 'audio' ? <FiMusic /> : <FiFile />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="msg-file">
        <span className="msg-file-icon">{icon}</span>
        <span className="msg-file-info">
          <span className="msg-file-name">{msg.file_name}</span>
          <span className="msg-file-size">{formatFileSize(msg.file_size)}</span>
        </span>
      </a>
    );
  };

  return (
    <div className="">
      <div className="chat-container">

        {/* ─── LEFT SIDEBAR ─── */}
        <div className={`chat-sidebar ${mobileShowChat ? 'hidden-mobile' : ''}`}>
          <div className="chat-sidebar-header">
            <h2>{t('chat.title')}</h2>
            <div className="chat-sidebar-actions">
              <button onClick={() => { setNewChatType('private'); setShowNewChat(true); }} title={t('chat.newChat')}>
                <FiPlus />
              </button>
              <button onClick={() => { setNewChatType('group'); setShowNewChat(true); }} title={t('chat.newGroup')}>
                <FiUsers />
              </button>
            </div>
          </div>

          <div className="chat-search">
            <div className="chat-search-wrap">
              <FiSearch />
              <input
                placeholder={t('chat.searchPlaceholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="chat-tabs">
            <button className={sidebarTab === 'all' ? 'active' : ''} onClick={() => setSidebarTab('all')}>{t('chat.tabAll')}</button>
            <button className={sidebarTab === 'private' ? 'active' : ''} onClick={() => setSidebarTab('private')}>{t('chat.tabDirect')}</button>
            <button className={sidebarTab === 'group' ? 'active' : ''} onClick={() => setSidebarTab('group')}>{t('chat.tabGroups')}</button>
          </div>

          <div className="chat-list">
            {filteredConversations.length === 0 && (
              <div className="empty-state" style={{ padding: '32px 16px' }}>
                <p>{t('chat.noConversations')}</p>
                <span style={{ fontSize: 13, color: 'var(--text-light)' }}>{t('chat.startNewChat')}</span>
              </div>
            )}
            {filteredConversations.map(conv => {
              const display = getConversationDisplay(conv, currentUserId, t);
              const isOnline = conv.type === 'private' ? getOtherUserOnline(conv) : false;
              const lastMsg = conv.last_message_text
                ? (conv.last_message_sender_name ? `${conv.last_message_sender_name.split(' ')[0]}: ` : '') +
                  (conv.last_message_type !== 'text' ? `[${conv.last_message_type}] ${conv.last_message_file_name || ''}` : conv.last_message_text)
                : t('chat.noMessagesYet');

              return (
                <div
                  key={conv.id}
                  className={`chat-list-item ${activeConvId === conv.id ? 'active' : ''}`}
                  onClick={() => selectConversation(conv.id)}
                >
                  <div className={`chat-avatar ${conv.type}`}>
                    {display.initials}
                    {conv.type === 'private' && (
                      <span className={`online-dot ${isOnline ? '' : 'offline'}`} />
                    )}
                  </div>
                  <div className="chat-list-info">
                    <div className="chat-list-name">{display.name}</div>
                    <div className="chat-list-preview">{lastMsg}</div>
                  </div>
                  <div className="chat-list-meta">
                    <span className="chat-list-time">{formatTime(conv.last_message_at || conv.created_at, t)}</span>
                    {conv.unread_count > 0 && (
                      <span className="chat-unread-badge">
                        {conv.unread_count > 99 ? '99+' : conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── MAIN CHAT AREA ─── */}
        <div className={`chat-main ${!mobileShowChat && activeConvId ? '' : ''} ${!mobileShowChat ? 'hidden-mobile' : ''}`}>
          {!activeConvId ? (
            <div className="chat-main-empty">
              <FiMessageSquare />
              <p>{t('chat.selectConversation')}</p>
              <span>{t('chat.chooseChatHint')}</span>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-left">
                  <button className="back-btn-mobile" onClick={() => setMobileShowChat(false)}>
                    <FiArrowLeft />
                  </button>
                  {activeConv && (() => {
                    const display = getConversationDisplay(activeConv, currentUserId, t);
                    const isOnline = activeConv.type === 'private' ? getOtherUserOnline(activeConv) : false;
                    return (
                      <>
                        <div className={`chat-avatar ${activeConv.type}`} style={{ width: 38, height: 38, minWidth: 38, fontSize: 14 }}>
                          {display.initials}
                          {activeConv.type === 'private' && (
                            <span className={`online-dot ${isOnline ? '' : 'offline'}`} />
                          )}
                        </div>
                        <div className="chat-header-info">
                          <h3>{display.name}</h3>
                          <span>
                            {activeConv.type === 'private'
                              ? isOnline ? <span className="online-text">{t('common.online')}</span> : t('common.offline')
                              : t('chat.membersCount', { count: (activeConv.members || []).length })
                            }
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="chat-header-actions">
                  {activeConv?.type === 'group' && (
                    <button onClick={() => setShowInfo(!showInfo)} title={t('chat.groupInfo')}>
                      <FiInfo />
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="chat-messages" ref={chatMessagesRef}>
                {hasMore && (
                  <button
                    className="btn btn-outline btn-sm load-more-btn"
                    onClick={() => messages[0] && loadMessages(activeConvId, messages[0].id)}
                    disabled={loadingMsgs}
                  >
                    {loadingMsgs ? t('common.loading') : t('chat.loadOlder')}
                  </button>
                )}

                {messageGroups.map(item => {
                  if (item.type === 'divider') {
                    return (
                      <div key={item.key} className="chat-date-divider">
                        <span>{formatDateDivider(item.date, t)}</span>
                      </div>
                    );
                  }

                  const msg = item.data;
                  const isMine = msg.sender_id === currentUserId;

                  return (
                    <div key={item.key} className={`chat-message-row ${isMine ? 'mine' : 'theirs'}`}>
                      {!isMine && (
                        <div className="msg-avatar">{getInitials(msg.sender_name)}</div>
                      )}
                      <div className="msg-bubble">
                        {!isMine && activeConv?.type === 'group' && (
                          <div className="msg-sender">{msg.sender_name}</div>
                        )}
                        {msg.text && (
                          <div className="msg-text">
                            {renderMessageText(msg.text, msg.mentions)}
                          </div>
                        )}
                        {msg.file_url && renderFileAttachment(msg)}
                        <div className="msg-time">{formatMessageTime(msg.created_at)}</div>
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>

              {/* Typing indicator */}
              <div className="chat-typing">{typingDisplay}</div>

              {/* Input area */}
              <div className="chat-input-area">
                {selectedFile && (
                  <div className="chat-file-preview">
                    <FiPaperclip />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedFile.name}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatFileSize(selectedFile.size)}</span>
                    <button onClick={() => setSelectedFile(null)}><FiX /></button>
                  </div>
                )}
                <div className="chat-input-wrap">
                  {/* Mention dropdown */}
                  {mentionSuggestions.length > 0 && mentionQuery !== null && (
                    <div className="chat-mention-dropdown">
                      {mentionSuggestions.map((m, idx) => (
                        <div
                          key={m.user_id}
                          className={`chat-mention-item ${idx === mentionIndex ? 'selected' : ''}`}
                          onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                        >
                          <div className="mention-avatar">{getInitials(m.name)}</div>
                          <div>
                            <div className="mention-name">{m.name}</div>
                            <div className="mention-email">{m.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files[0]) {
                        const f = e.target.files[0];
                        if (f.size > 25 * 1024 * 1024) {
                          toast.error(t('chat.fileTooLarge'));
                          return;
                        }
                        setSelectedFile(f);
                      }
                      e.target.value = '';
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg,.mp3,.wav,.ogg,.m4a,.aac"
                  />

                  <button
                    className="chat-input-btn file-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title={t('chat.attachFile')}
                  >
                    <FiPaperclip />
                  </button>

                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={activeConv?.type === 'group' ? t('chat.typeMessageMention') : t('chat.typeMessage')}
                    rows={1}
                    onInput={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />

                  <button
                    className="chat-input-btn send-btn"
                    onClick={sendMessage}
                    disabled={!inputText.trim() && !selectedFile}
                    title={t('chat.send')}
                  >
                    <FiSend />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ─── GROUP INFO PANEL ─── */}
        {showInfo && activeConv?.type === 'group' && (
          <div className="chat-info-panel">
            <div className="chat-info-header">
              <h3>{t('chat.groupInfo')}</h3>
              <button onClick={() => setShowInfo(false)}><FiX /></button>
            </div>
            <div className="chat-info-body">
              <div className="chat-info-avatar" style={{ background: 'linear-gradient(135deg, var(--teal), #0d9488)' }}>
                {activeConv.name ? activeConv.name[0].toUpperCase() : 'G'}
              </div>
              <div className="chat-info-name">{activeConv.name}</div>
              {activeConv.description && (
                <div className="chat-info-desc">{activeConv.description}</div>
              )}

              <div className="chat-info-section">
                <h4>{t('chat.membersLabel', { count: (activeConv.members || []).length })}</h4>
                {(activeConv.members || []).map(member => {
                  const myRole = (activeConv.members || []).find(m => m.user_id === currentUserId)?.role;
                  return (
                    <div key={member.user_id} className="chat-info-member">
                      <div className="member-avatar">{getInitials(member.name)}</div>
                      <div className="member-info">
                        <div className="member-name">
                          {member.name}
                          {member.user_id === currentUserId && ` (${t('chat.you')})`}
                        </div>
                        <div className="member-role">
                          {member.role}
                          {onlineUserIds.includes(member.user_id) && ` · ${t('common.online')}`}
                        </div>
                      </div>
                      {myRole === 'admin' && member.user_id !== currentUserId && (
                        <button
                          className="member-remove"
                          onClick={() => removeMember(member.user_id)}
                          title={t('chat.removeMember')}
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── NEW CHAT MODAL ─── */}
      {showNewChat && (
        <div className="chat-new-modal" onClick={(e) => { if (e.target === e.currentTarget) closeNewChatModal(); }}>
          <div className="chat-new-modal-content">
            <div className="chat-new-modal-header">
              <h3>{newChatType === 'group' ? t('chat.createGroup') : t('chat.newChat')}</h3>
              <button onClick={closeNewChatModal}><FiX /></button>
            </div>

            <div className="chat-new-modal-body">
              {newChatType === 'group' && (
                <>
                  <div className="form-group">
                    <label>{t('chat.groupNameLabel')} <span className="required">*</span></label>
                    <input
                      value={newGroupName}
                      onChange={e => setNewGroupName(e.target.value)}
                      placeholder={t('chat.groupNamePlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.description')}</label>
                    <input
                      value={newGroupDesc}
                      onChange={e => setNewGroupDesc(e.target.value)}
                      placeholder={t('chat.descriptionPlaceholder')}
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>
                  {newChatType === 'group' ? t('chat.addMembers') : t('chat.selectUser')}
                  {selectedUserIds.length > 0 && ` (${t('chat.selectedCount', { count: selectedUserIds.length })})`}
                </label>
                <input
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder={t('chat.searchUsers')}
                  style={{ marginBottom: 8 }}
                />
                <div className="user-select-list">
                  {filteredUsers.map(u => (
                    <div
                      key={u.user_id}
                      className={`user-select-item ${selectedUserIds.includes(u.user_id) ? 'selected' : ''}`}
                      onClick={() => toggleUserSelect(u.user_id)}
                    >
                      <div className="user-check">
                        {selectedUserIds.includes(u.user_id) && <FiCheck />}
                      </div>
                      <div className={`chat-avatar private`} style={{ width: 32, height: 32, minWidth: 32, fontSize: 12 }}>
                        {getInitials(u.name)}
                        {onlineUserIds.includes(u.user_id) && <span className="online-dot" style={{ width: 8, height: 8 }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      {t('chat.noUsersFound')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="chat-new-modal-footer">
              <button className="btn btn-outline" onClick={closeNewChatModal}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={createConversation}>
                {newChatType === 'group' ? t('chat.createGroup') : t('chat.startChat')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
